<template>
  <main class="flex-grow">
    <section class="container mx-auto px-4 py-5 sm:px-6 lg:py-8">
      <div class="mx-auto max-w-5xl">
        <div class="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p class="mb-1.5 text-xs font-semibold uppercase tracking-wide text-indigo-600 dark:text-indigo-300">
              Post-finals schedule
            </p>
            <h1 class="text-2xl font-bold text-slate-950 dark:text-white sm:text-4xl">
              Special Schedule
            </h1>
          </div>

          <div class="rounded-xl border border-slate-200 bg-white/85 p-1 shadow-lg dark:border-slate-700 dark:bg-slate-900/85">
            <div class="grid grid-cols-2 gap-1">
              <button
                v-for="option in gradeOptions"
                :key="option.value"
                type="button"
                :class="gradeButtonClass(option.value)"
                @click="setGrade(option.value)"
              >
                {{ option.label }}
              </button>
            </div>
          </div>
        </div>

        <div v-if="loading" class="flex min-h-[360px] items-center justify-center">
          <div class="h-16 w-16 animate-spin rounded-full border-b-4 border-t-4 border-indigo-500 dark:border-indigo-300"></div>
        </div>

        <div
          v-else-if="error"
          class="rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-red-700 shadow-lg dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-200"
        >
          {{ error }}
        </div>

        <div v-else class="grid gap-3">
          <article
            v-for="item in timeline"
            :key="item.id"
            class="grid grid-cols-[4.5rem_minmax(0,1fr)] gap-4 sm:grid-cols-[6rem_minmax(0,1fr)] sm:gap-5"
          >
            <div class="relative pt-2 text-right">
              <div
                v-if="!item.isLast"
                class="absolute right-[0.45rem] top-7 h-[calc(100%+0.75rem)] w-px bg-slate-300 dark:bg-slate-700"
              ></div>
              <time class="block text-xs font-semibold text-slate-700 dark:text-slate-300 sm:text-sm">
                {{ item.time }}
              </time>
            </div>

            <div class="relative">
              <div :class="['absolute -left-[1.03rem] top-4 h-4 w-4 rounded-full border-4 sm:-left-[1.17rem]', item.dotClass]"></div>
              <div :class="['rounded-xl border p-3 shadow-lg transition-colors sm:p-4', item.cardClass]">
                <div class="flex flex-col gap-2.5 sm:flex-row sm:items-start sm:justify-between">
                  <div class="min-w-0">
                    <div class="mb-1.5 flex flex-wrap items-center gap-2">
                      <span :class="['inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold', item.badgeClass]">
                        {{ item.badge }}
                      </span>
                      <span v-if="item.slotLabel" class="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                        {{ item.slotLabel }}
                      </span>
                    </div>
                    <h2 class="text-lg font-bold text-slate-950 dark:text-white sm:text-xl">
                      {{ item.title }}
                    </h2>
                    <p v-if="item.subtitle" class="mt-0.5 text-xs text-slate-600 dark:text-slate-300 sm:text-sm">
                      {{ item.subtitle }}
                    </p>
                  </div>

                  <div
                    v-if="item.room"
                    class="shrink-0 rounded-lg border border-slate-200 bg-white/80 px-2.5 py-1.5 text-xs font-semibold text-slate-700 dark:border-slate-700 dark:bg-slate-950/50 dark:text-slate-200 sm:text-sm"
                  >
                    {{ item.room }}
                  </div>
                </div>
              </div>
            </div>
          </article>
        </div>
      </div>
    </section>
  </main>
</template>

<script>
import axios from 'axios';
import { consumeTokenFromUrl, getActiveAuthToken, redirectToLogin as beginLogin } from '@/config/auth';
import { backendUrl } from '@/config/backend';

const GRADE_STORAGE_KEY = 'collegiate_special_grade';

const gradeOptions = [
  { value: '9-10', label: 'Grades 9/10' },
  { value: '11', label: 'Grade 11' },
];

function readInitialGrade() {
  if (typeof window === 'undefined') {
    return '9-10';
  }

  try {
    const storedGrade = window.localStorage.getItem(GRADE_STORAGE_KEY);
    return storedGrade === '11' ? '11' : '9-10';
  } catch (error) {
    console.warn('Unable to read special schedule grade:', error);
    return '9-10';
  }
}

const scheduleTemplate = [
  { id: 'greek', time: '9:00-9:30', label: 'Greek II', resolver: 'greek' },
  { id: 'a', time: '9:40-10:10', label: 'A Block', block: 'A' },
  { id: 'assembly', time: '10:15-10:35', label: 'Assembly', title: 'Assembly', type: 'event' },
  { id: 'advisory', time: '10:40-11:00', label: 'Advisory', title: 'Advisory', type: 'event', variant: 'advisory' },
  { id: 'e', time: '11:05-11:35', label: 'E Block', block: 'E' },
  { id: 'first-lunch-wave', time: '11:40-12:10', label: 'F Block / Lunch', resolver: 'firstLunchWave' },
  { id: 'second-lunch-wave', time: '12:15-12:45', label: 'F Block / Lunch', resolver: 'secondLunchWave' },
  { id: 'd', time: '12:50-1:20', label: 'D Block', block: 'D' },
  { id: 'g', time: '1:25-1:55', label: 'G Block', block: 'G' },
  { id: 'c', time: '2:00-2:30', label: 'C Block', block: 'C' },
  { id: 'dismissal', time: '2:30', label: 'Dismissal', title: 'Dismissal', type: 'dismissal' },
];

const typeStyles = {
  class: {
    badge: 'Class',
    badgeClass: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-200',
    cardClass: 'border-indigo-200 bg-white/90 dark:border-indigo-900/70 dark:bg-slate-900/90',
    dotClass: 'border-indigo-100 bg-indigo-600 dark:border-slate-950 dark:bg-indigo-400',
  },
  greek: {
    badge: 'Class',
    badgeClass: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-200',
    cardClass: 'border-indigo-200 bg-white/90 dark:border-indigo-900/70 dark:bg-slate-900/90',
    dotClass: 'border-indigo-100 bg-indigo-600 dark:border-slate-950 dark:bg-indigo-400',
  },
  event: {
    badge: 'Event',
    badgeClass: 'bg-violet-100 text-violet-700 dark:bg-violet-950 dark:text-violet-200',
    cardClass: 'border-violet-200 bg-white/90 dark:border-violet-900/70 dark:bg-slate-900/90',
    dotClass: 'border-violet-100 bg-violet-600 dark:border-slate-950 dark:bg-violet-400',
  },
  advisory: {
    badge: 'Event',
    badgeClass: 'bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-200',
    cardClass: 'border-slate-200 bg-white/90 dark:border-slate-700 dark:bg-slate-900/90',
    dotClass: 'border-slate-100 bg-slate-500 dark:border-slate-950 dark:bg-slate-400',
  },
  lunch: {
    badge: 'Lunch',
    badgeClass: 'bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-200',
    cardClass: 'border-slate-200 bg-white/90 dark:border-slate-700 dark:bg-slate-900/90',
    dotClass: 'border-slate-100 bg-slate-500 dark:border-slate-950 dark:bg-slate-400',
  },
  free: {
    badge: 'Free',
    badgeClass: 'bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-200',
    cardClass: 'border-slate-200 bg-white/80 dark:border-slate-700 dark:bg-slate-900/75',
    dotClass: 'border-slate-100 bg-slate-400 dark:border-slate-950 dark:bg-slate-500',
  },
  dismissal: {
    badge: 'Done',
    badgeClass: 'bg-slate-900 text-white dark:bg-white dark:text-slate-950',
    cardClass: 'border-slate-300 bg-slate-100 dark:border-slate-700 dark:bg-slate-800',
    dotClass: 'border-slate-100 bg-slate-900 dark:border-slate-950 dark:bg-white',
  },
};

export default {
  name: 'SpecialSchedule',
  data() {
    return {
      token: null,
      sections: [],
      grade: readInitialGrade(),
      loading: true,
      error: '',
      gradeOptions,
    };
  },
  computed: {
    classMatches() {
      const sections = this.dedupeSections(this.sections);
      const greek = sections.find((section) => this.sectionName(section).includes('greek')) || null;
      const blockSections = {};

      sections.forEach((section) => {
        if (greek && this.sectionKey(section) === this.sectionKey(greek)) {
          return;
        }

        const letter = this.blockLetter(section);
        if (!letter || letter === 'B' || blockSections[letter]) {
          return;
        }

        blockSections[letter] = section;
      });

      return { greek, blockSections };
    },
    timeline() {
      return scheduleTemplate.map((slot, index) => ({
        ...this.resolveSlot(slot),
        id: slot.id,
        time: slot.time,
        slotLabel: slot.label,
        isLast: index === scheduleTemplate.length - 1,
      }));
    },
  },
  created() {
    this.extractToken();
    this.fetchClasses();
  },
  methods: {
    setGrade(grade) {
      this.grade = grade === '11' ? '11' : '9-10';

      try {
        window.localStorage.setItem(GRADE_STORAGE_KEY, this.grade);
      } catch (error) {
        console.warn('Unable to save special schedule grade:', error);
      }
    },
    gradeButtonClass(value) {
      const isActive = this.grade === value;
      return [
        'rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors sm:px-4 sm:text-sm',
        isActive
          ? 'bg-indigo-600 text-white shadow-md dark:bg-indigo-500'
          : 'text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800',
      ];
    },
    extractToken() {
      const token = consumeTokenFromUrl() || getActiveAuthToken();

      if (token) {
        this.token = token;
      } else {
        beginLogin({ originalUrl: window.location.href });
      }
    },
    isTokenExpired(token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const expiry = payload.exp;
        const now = Math.floor(new Date().getTime() / 1000);
        return now > expiry;
      } catch (error) {
        console.error('Invalid token format:', error);
        return true;
      }
    },
    async fetchClasses() {
      if (!this.token || this.isTokenExpired(this.token)) {
        beginLogin({ originalUrl: window.location.href });
        return;
      }

      try {
        const response = await axios.get(backendUrl('/classes'), {
          headers: { Authorization: `Bearer ${this.token}` },
        });

        this.sections = Array.isArray(response.data?.value) ? response.data.value : [];
      } catch (error) {
        console.error('Error fetching classes:', error);
        if (error.response && error.response.status === 401) {
          beginLogin({ originalUrl: window.location.href });
          return;
        }
        this.error = 'Failed to load your classes.';
      } finally {
        this.loading = false;
      }
    },
    dedupeSections(sections) {
      const seen = new Set();

      return sections.filter((section) => {
        const key = this.sectionKey(section);
        if (seen.has(key)) {
          return false;
        }

        seen.add(key);
        return true;
      });
    },
    sectionKey(section) {
      return String(
        section?.section_id ||
        `${section?.block_name || ''}-${section?.section_display_name || ''}-${section?.room_name || ''}`,
      );
    },
    sectionName(section) {
      return String(section?.section_display_name || '').toLowerCase();
    },
    blockLetter(section) {
      const blockName = String(section?.block_name || '').trim();
      return blockName.match(/^([A-G])/i)?.[1]?.toUpperCase() || null;
    },
    resolveSlot(slot) {
      if (slot.block) {
        return this.resolveClassSlot(slot.block);
      }

      if (slot.resolver === 'greek') {
        return this.resolveGreekSlot();
      }

      if (slot.resolver === 'firstLunchWave') {
        return this.grade === '11' ? this.resolveLunchSlot() : this.resolveClassSlot('F');
      }

      if (slot.resolver === 'secondLunchWave') {
        return this.grade === '11' ? this.resolveClassSlot('F') : this.resolveLunchSlot();
      }

      return this.applyTypeStyles({
        title: slot.title,
        type: slot.variant || slot.type,
      });
    },
    resolveClassSlot(letter) {
      const section = this.classMatches.blockSections[letter];
      if (!section) {
        return this.resolveFreeSlot();
      }

      return this.applyTypeStyles({
        title: section.section_display_name || `${letter} Block`,
        subtitle: `${letter} Block`,
        room: this.normalizeRoom(section.room_name),
        type: 'class',
      });
    },
    resolveGreekSlot() {
      const section = this.classMatches.greek;
      if (!section) {
        return this.resolveFreeSlot();
      }

      return this.applyTypeStyles({
        title: section.section_display_name || 'Greek II',
        subtitle: 'Greek II',
        room: this.normalizeRoom(section.room_name),
        type: 'greek',
      });
    },
    resolveLunchSlot() {
      return this.applyTypeStyles({
        title: 'Lunch',
        type: 'lunch',
      });
    },
    resolveFreeSlot() {
      return this.applyTypeStyles({
        title: 'Free Period',
        type: 'free',
      });
    },
    normalizeRoom(roomName) {
      const room = String(roomName || '').trim();
      return room || null;
    },
    applyTypeStyles(item) {
      const styles = typeStyles[item.type] || typeStyles.class;

      return {
        ...item,
        badge: styles.badge,
        badgeClass: styles.badgeClass,
        cardClass: styles.cardClass,
        dotClass: styles.dotClass,
      };
    },
  },
};
</script>
